import {daySectionLabel, scheduleEndedBeforeBusinessToday} from './schedule-language'
import type {TerminalRowEntry} from './terminal-jobs'
import type {ScheduleDate, ViewRange} from './types'

export type RiskWindowGroup = {
    label: string | null
    entries: TerminalRowEntry[]
}

function riskWindowGroupLabel(
    entry: TerminalRowEntry,
    visibleDays: ScheduleDate[],
    viewRange: ViewRange,
): string {
    const offset = entry.viewDayOffset
    const day = visibleDays[offset]
    if (!day) {
        return 'Later this week'
    }

    if (day.isToday) {
        return 'Today'
    }

    const todayOffset = visibleDays.findIndex((visibleDay) => visibleDay.isToday)
    if (todayOffset >= 0 && offset === todayOffset + 1) {
        return 'Tomorrow'
    }

    if (viewRange === 'week' && todayOffset >= 0 && offset > todayOffset + 1) {
        return 'Later this week'
    }
    if (
        viewRange === 'month' &&
        todayOffset >= 0 &&
        offset > todayOffset + 1 &&
        offset <= todayOffset + 6
    ) {
        return 'Later this week'
    }

    return daySectionLabel(day, viewRange)
}

type RiskWindowBucket = RiskWindowGroup & {
    sortOffset: number
}

/** Groups lane entries into day bands within each operational lane. */
export function groupByRiskWindow(
    entries: TerminalRowEntry[],
    viewRange: ViewRange,
    visibleDays: ScheduleDate[],
): RiskWindowGroup[] {
    if (entries.length === 0) {
        return [{label: null, entries}]
    }

    const buckets = new Map<string, RiskWindowBucket>()
    for (const entry of entries) {
        const overdue = scheduleEndedBeforeBusinessToday(entry.job.schedule)
        const label = overdue ? 'Overdue' : riskWindowGroupLabel(entry, visibleDays, viewRange)
        const bucket = buckets.get(label)
        if (bucket) {
            bucket.entries.push(entry)
            bucket.sortOffset = Math.min(bucket.sortOffset, entry.viewDayOffset)
            continue
        }
        buckets.set(label, {
            label,
            entries: [entry],
            sortOffset: overdue ? Number.NEGATIVE_INFINITY : entry.viewDayOffset,
        })
    }

    return [...buckets.values()]
        .sort((a, b) => a.sortOffset - b.sortOffset)
        .map(({label, entries: bucketEntries}) => ({
            label,
            entries: [...bucketEntries].sort(byDayThenCustomerThenStart),
        }))
}

function byDayThenCustomerThenStart(a: TerminalRowEntry, b: TerminalRowEntry): number {
    return (
        a.viewDayOffset - b.viewDayOffset ||
        a.job.customer.localeCompare(b.job.customer, undefined, {sensitivity: 'base'}) ||
        (a.job.schedule?.startTime ?? '99').localeCompare(b.job.schedule?.startTime ?? '99') ||
        a.job.jobCode.localeCompare(b.job.jobCode)
    )
}
